import React, { FC, useState } from 'react';
import { EBottomSheetAction, TBottomSheetAction } from '../CourierBottomSheet';
import { useDeleteCourierMutation, useDisconnectCourierMutation } from '../../queries';
import {
  trackInvitationDisconnectSuccess,
  trackInvitationRemoveSuccess,
} from '../../tracking/invitation';
import { useQueryClient } from 'react-query';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import { NotificationCategories, useNotificationsActions } from '@src/shared/context/notifications';
import useSanitizedTranslation from '@src/shared/hooks/use-sanitized-translation';
import { encryptHash } from '@src/shared/utils';
import styles from './CourierDetailActionsDelete.module.scss';
import { IconClose } from '@justeattakeaway/pie-icons-react';
import PIEText from '@src/shared/components/PIEText';
import PIEButton from '@src/shared/components/PIEButton';
interface ICourierDetailActionsDelete {
  onAction: () => any;
  onDismiss: () => any;
  action: TBottomSheetAction;
}

export const CourierDetailActionsDelete: FC<ICourierDetailActionsDelete> = ({
  onDismiss,
  onAction,
  action,
}) => {
  const [isPromising, setIsPromising] = useState<boolean>();

  const { t } = useSanitizedTranslation();

  const queryClient = useQueryClient();
  const { notifyInformation, notifyError } = useNotificationsActions(
    NotificationCategories.CourierManagement
  );

  /* Context State */
  const { restaurant } = useKeycloakUserState();

  const deleteCourierMutation = useDeleteCourierMutation({
    onSuccess: () => {
      notifyInformation(t('manage_couriers.COURIER_REMOVE_SUCCESS_MESSAGE'), '');
      trackInvitationRemoveSuccess(
        restaurant?.language,
        restaurant?.country,
        '',
        1 // We are removeing multi couirer selection
      );
      queryClient.invalidateQueries('couriers');
    },
    onError: () => {
      setIsPromising(false);
      notifyError(t('manage_couriers.commonError'), '');
    },
    onSettled: () => {
      setIsPromising(false);
      onDismiss();
    },
  });

  const disconnectCourierMutation = useDisconnectCourierMutation({
    onSuccess: (data: any) => {
      trackInvitationDisconnectSuccess(
        restaurant?.language,
        restaurant?.country,
        encryptHash(data.email)
      );
      notifyInformation(t('manage_couriers.COURIER_DISCONNECT_SUCCESS_MESSAGE'), '');
      queryClient.invalidateQueries('couriers');
    },
    onError: () => {
      setIsPromising(false);
      notifyError(t('manage_couriers.commonError'), '');
    },
    onSettled: () => {
      setIsPromising(false);
      onDismiss();
    },
  });

  const onLinkCourierAction = async ({
    courierHash,
    actionStatus,
  }: {
    courierHash: string;
    actionStatus: EBottomSheetAction;
  }) => {
    setIsPromising(true);
    actionStatus === EBottomSheetAction.Disconnect
      ? disconnectCourierMutation.mutate(courierHash)
      : deleteCourierMutation.mutate(courierHash);
  };

  return (
    <>
      <div className={styles.courierDetailActionsDeleteContainer}>
        <div className={styles.courierDetailDeleteActionsClose} onClick={onDismiss}>
          <IconClose className={styles.courierDetailActionsDeleteIcon} size="m" />
        </div>
        <div className={styles.courierDetailActionsDeleteHeader}>
          <PIEText
            size={'var(--xds-font-size-24)'}
            fontWeight="var(--xds-font-weight-extrabold)"
            text={
              action.type === EBottomSheetAction.Disconnect
                ? t('manage_couriers.DISCONNECT_TITLE')
                : t('manage_couriers.REMOVE_COURIER_TITLE')
            }
          />
        </div>
        <div className={styles.courierDetailActionsDeleteContent}>
          <PIEText
            size={'var(--xds-font-size-16)'}
            text={t(
              `${
                action.type === EBottomSheetAction.Disconnect
                  ? 'manage_couriers.DISCONNECT_COURIER_DESCRIPTION'
                  : 'manage_couriers.REMOVE_COURIER_DESCRIPTION'
              }`,
              {
                courierName: action.source.text,
              }
            )}
          />
        </div>
        <div className={styles.courierDetailActionsDeleteFooter}>
          <PIEButton
            onClick={onDismiss}
            size="medium"
            mode="ghost"
            text={
              action.type === EBottomSheetAction.Disconnect
                ? t('manage_couriers.REMOVE_NOTNOW')
                : t('manage_couriers.REMOVE_CANCEL')
            }
            type={'button'}
          ></PIEButton>
          <PIEButton
            onClick={() =>
              onLinkCourierAction({
                courierHash:
                  action.type === EBottomSheetAction.Disconnect
                    ? (action.source?.id as any)
                    : action.source?.hash,
                actionStatus: action.type,
              })
            }
            mode="destructive"
            size="medium"
            isPromising={isPromising}
            text={t('manage_couriers.REMOVE_CONFIRM')}
            type={'button'}
          ></PIEButton>
        </div>
      </div>
    </>
  );
};
